import useBoundary from 'hooks/useBoundary';
import useField from 'hooks/useField';

import type {
    BoundaryFieldSchema,
    SaveBoundaryAssignments,
    UseFieldBoundaryAssignmentsReturn
} from './types';

const getBoundaryValues = (
    boundaries: BoundaryFieldSchema[],
    primary: BoundaryFieldSchema | null
): string[] => {
    const values = boundaries.map(({ value }) => value).filter(Boolean);

    return primary?.value ? [...new Set([...values, primary.value])] : [...new Set(values)];
};

const useFieldBoundaryAssignments = (): UseFieldBoundaryAssignmentsReturn => {
    const {
        isLoading: isBoundaryLoading,
        setPrimaryBoundary,
        unassignFieldFromBoundary
    } = useBoundary();
    const { assignFieldToBoundary, isLoading: isFieldLoading } = useField();

    // Runs sequentially and stops at the first failure: there is no transactional endpoint, so
    // continuing past an error would leave the field in a state the form no longer describes.
    // Assignments come first and the primary is set last, so the backend's auto-promotion when a
    // primary is unassigned cannot overwrite an explicit primary choice.
    const saveBoundaryAssignments: SaveBoundaryAssignments = async ({
        boundaries,
        initialBoundaries,
        initialPrimary,
        primary,
        siteId
    }) => {
        const initialValues = getBoundaryValues(initialBoundaries, initialPrimary);
        const nextValues = getBoundaryValues(boundaries, primary);

        const valuesToAssign = nextValues.filter(value => !initialValues.includes(value));
        const valuesToUnassign = initialValues.filter(value => !nextValues.includes(value));

        for (const boundaryId of valuesToAssign) {
            const isAssigned = await assignFieldToBoundary({ boundaryId, siteId });

            if (!isAssigned) {
                return false;
            }
        }

        for (const boundaryId of valuesToUnassign) {
            const isUnassigned = await unassignFieldFromBoundary({ boundaryId, siteId });

            if (!isUnassigned) {
                return false;
            }
        }

        const hasNewPrimary = !!primary?.value && primary.value !== initialPrimary?.value;

        if (hasNewPrimary && !(await setPrimaryBoundary({ id: primary.value }))) {
            return false;
        }

        return true;
    };

    return {
        isLoading: isBoundaryLoading || isFieldLoading,
        saveBoundaryAssignments
    };
};

export default useFieldBoundaryAssignments;
